import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Crown, Users, Database, TrendingUp, ArrowRight } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface UpgradePlansModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?: string;
  currentUsage?: {
    users: number;
    licenses: number;
    applications: number;
  };
}

interface UpgradePlan {
  id: number;
  name: string;
  price: number;
  priceId: string;
  features: string[];
  limits: {
    users: number;
    licenses: number;
    applications: number;
  };
}

export function UpgradePlansModal({ 
  isOpen, 
  onClose, 
  currentPlan = "Free",
  currentUsage = { users: 0, licenses: 0, applications: 0 }
}: UpgradePlansModalProps) {
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<UpgradePlan | null>(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Fetch available upgrade plans
  const { data: upgradeData, isLoading } = useQuery({
    queryKey: ['/api/subscription/upgrade-options'],
    enabled: isOpen,
  });

  const upgradeMutation = useMutation({
    mutationFn: async (planData: { planId: number; priceId: string }) => {
      const response = await apiRequest('/api/subscription/upgrade-checkout', {
        method: 'POST',
        body: planData
      });
      return response;
    },
    onSuccess: (data) => {
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      }
    },
    onError: (error) => {
      toast({
        title: "Upgrade Failed",
        description: "Failed to start the upgrade process. Please try again.",
        variant: "destructive",
      });
      setIsUpgrading(false);
    },
  });

  const handleUpgrade = (plan: UpgradePlan) => {
    setSelectedPlan(plan);
    setIsUpgrading(true);
    upgradeMutation.mutate({ planId: plan.id, priceId: plan.priceId });
  };

  const formatLimit = (limit: number): string => {
    return limit === Infinity ? "Unlimited" : limit.toString();
  };

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'single': return <Users className="w-5 h-5" />;
      case 'team': return <Database className="w-5 h-5" />;
      case 'enterprise': return <Crown className="w-5 h-5" />;
      default: return <TrendingUp className="w-5 h-5" />;
    }
  };

  const getPlanColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'single': return 'from-blue-50 to-cyan-50 border-blue-200';
      case 'team': return 'from-green-50 to-emerald-50 border-green-200';
      case 'enterprise': return 'from-purple-50 to-violet-50 border-purple-200';
      default: return 'from-gray-50 to-slate-50 border-gray-200';
    }
  };

  const getPlanButtonColor = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'single': return 'bg-blue-600 hover:bg-blue-700';
      case 'team': return 'bg-green-600 hover:bg-green-700';
      case 'enterprise': return 'bg-purple-600 hover:bg-purple-700';
      default: return 'bg-gray-600 hover:bg-gray-700';
    }
  };

  const availablePlans = upgradeData?.upgradePlans || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Upgrade Your Plan</DialogTitle>
          <DialogDescription>
            Choose the plan that best fits your organization's needs
          </DialogDescription>
        </DialogHeader>

        {/* Current Usage Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-gray-900 mb-2">Current Usage</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="text-center">
              <div className="font-semibold text-gray-900">{currentUsage.users}</div>
              <div className="text-gray-600">Users</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-900">{currentUsage.licenses}</div>
              <div className="text-gray-600">Licenses</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-gray-900">{currentUsage.applications}</div>
              <div className="text-gray-600">Applications</div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {availablePlans.map((plan: UpgradePlan) => (
              <Card key={plan.id} className={`relative bg-gradient-to-br ${getPlanColor(plan.name)}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`p-2 rounded-lg ${plan.name.toLowerCase() === 'enterprise' ? 'bg-purple-100' : plan.name.toLowerCase() === 'team' ? 'bg-green-100' : 'bg-blue-100'}`}>
                        {getPlanIcon(plan.name)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{plan.name}</CardTitle>
                        {plan.name.toLowerCase() === 'team' && (
                          <Badge variant="secondary" className="text-xs">Most Popular</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900">
                    ${plan.price}
                    <span className="text-sm font-normal text-gray-600">/month</span>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Plan Limits */}
                  <div className="bg-white/60 rounded-lg p-3 space-y-2">
                    <h5 className="font-medium text-gray-900 text-sm">Plan Limits</h5>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="text-center">
                        <div className="font-semibold">{formatLimit(plan.limits.users)}</div>
                        <div className="text-gray-600">Users</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">{formatLimit(plan.limits.licenses)}</div>
                        <div className="text-gray-600">Licenses</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold">{formatLimit(plan.limits.applications)}</div>
                        <div className="text-gray-600">Apps</div>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2">
                    {plan.features.slice(0, 4).map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                    {plan.features.length > 4 && (
                      <div className="text-xs text-gray-500">
                        +{plan.features.length - 4} more features
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => handleUpgrade(plan)}
                    disabled={isUpgrading}
                    className={`w-full ${getPlanButtonColor(plan.name)}`}
                  >
                    {isUpgrading && selectedPlan?.id === plan.id ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span>Upgrade to {plan.name}</span>
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {availablePlans.length === 0 && !isLoading && (
          <div className="text-center py-8">
            <Crown className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">You're on the highest plan!</h3>
            <p className="text-gray-600">You already have access to all available features.</p>
          </div>
        )}

        <div className="text-center pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            All plans include email support and license renewal tracking.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}